import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { LEAD_DROP_POPUP_SECTION_VARIANTS } from "../../../constants";
import { handleCloseLeaddrop } from "@/store/slices/leaddrop";
import { trackCrossClick, trackPopupOpen } from "@/customAnalytics/rudderStackEvents";
import type { IUniversitySettings } from "@/api/admin/universitySettings";
import type { ILeadDropPopup } from "@/api/admin/leadDropPopup";
import type { ICourse } from "@/api/admin/course";
import { ILeaddropFormData } from "@/store/slices/leaddrop";
import { UNIVERSITY_ID } from "@/constants/admin/sectionTypes";
/**
 * Loaded on demand, not statically.
 *
 * This is a modal: it renders only when `customPopup` resolves AND the visitor has opened
 * it, so it has no SSR value. Imported statically it pulled its whole validation chain --
 * ConfigLeadFormPopup -> lib/validation -> zod -- into the first-load bundle of EVERY
 * public page, roughly 107 KB gzipped, on tenants where no custom form is configured and
 * the component can therefore never render at all.
 *
 * Same `dynamic(..., { ssr: false })` shape the lead-form registry already uses for its
 * surfaces (see widgets/leaddropform/InlineLeadForm.tsx). Trade-off accepted there and
 * here: the chunk is fetched when the popup first opens, so on a cold cache and a slow
 * connection there is a brief gap before it paints.
 */
const ConfigLeadFormPopup = dynamic(
  () => import("@/components/organisms/leadForm/ConfigLeadFormPopup"),
  { ssr: false },
);
import { useResolvedLeadForm } from "@/components/organisms/leadForm/useResolvedLeadForm";
import LeaddropDelay5026Dialog from "@/widgets/leaddropdelay";

interface DefaultValues {
  phone?: string;
  name?: string;
  email?: string;
  qualification?: string;
  interest?: string;
  course?: string;
  media?: string;
  campaign?: string;
  termsAccepted?: boolean;
}

interface LeaddropComponentProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  onClose?: () => void;
  data?:
    | {
        formData?: ILeadDropPopup | null;
        courses?: ICourse[];
        defaultValues?: DefaultValues;
      }
    | ILeadDropPopup
    | null;
  variant?: string;
  mobileData?: unknown;
  isMobile?: boolean;
  enrollmentBoundCourseSelectValue?: string | null;
  lockEnrollmentCourse?: boolean;
}

const OpenLeaddrop = ({
  leaddropPopup,
}: {
  leaddropPopup: IUniversitySettings | null;
}) => {
  const dispatch = useDispatch();

  const leaddropState = useSelector((state: RootState) => state.leaddrop);

  const { formData, courses, qualification, defaultValues, mobileData, isMobile } =
    leaddropState;

  const {
    isOpen,
    variant,
    downloadLink,
    isExitFormOpen,
    isDelayFormOpen,
    lockEnrollmentCourse,
    enrollmentBoundCourseSelectValue,
  } = leaddropState;

  /**
   * Lead popup 8 only: when the opener didn't set an explicit course binding in
   * the slice, fall back to the page's linked course (page.course_id →
   * globalslice, same derivation as LeaddropForm8). This makes the verified
   * user × conflicting-course re-registration flow reachable from the popup;
   * other variants keep their slice-only binding.
   */
  const pageCourse = useSelector(
    (state: RootState) => state.globalslice.course,
  ) as ICourse | undefined;
  // "8" = LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_8 (sectionTypes).
  const isLeadPopup8 = String(leaddropPopup?.variant ?? "") === "8";
  const pageCourseKey = useMemo(() => {
    const erp = String(pageCourse?.erp_unicode ?? "").trim();
    if (erp) return erp;
    const id = pageCourse?.id != null ? String(pageCourse.id).trim() : "";
    return id || null;
  }, [pageCourse]);
  const effectiveLockEnrollmentCourse =
    lockEnrollmentCourse || (isLeadPopup8 && Boolean(pageCourseKey));
  const effectiveBoundCourseSelectValue = lockEnrollmentCourse
    ? enrollmentBoundCourseSelectValue
    : isLeadPopup8
      ? pageCourseKey
      : enrollmentBoundCourseSelectValue;

  /**
   * A CMS-authored popup form for this page, if one is configured. Surface-gated, so a
   * config authored as an inline form never hijacks the popup.
   */
  const customPopup = useResolvedLeadForm("popup");

  const LeaddropComponent = useMemo(() => {
    if (!leaddropPopup?.section || !leaddropPopup?.variant) {
      return null;
    }

    return LEAD_DROP_POPUP_SECTION_VARIANTS[leaddropPopup.variant] as
      | React.ComponentType<LeaddropComponentProps>
      | undefined;
  }, [leaddropPopup]);

  // ─── RUDDERSTACK (new, sheet-aligned) — remove block + delete rudderStackEvents.ts to revert ───
  // popup_open / cross_click: fire on the drawer's display transitions —
  // popup_open when it becomes displayed (isOpen && a popup variant exists),
  // cross_click when it closes. Both re-fire on each open/close cycle. The
  // popup name captured at open is reused for the close so they stay consistent.
  // NOTE: cross_click here fires on ANY close (× button, backdrop, ESC), not the
  // × button only. DOUBT: confirm popup_name / component_name scheme.
  // The custom popup counts as displayed exactly like a legacy variant — otherwise the
  // whole funnel silently loses popup_open/cross_click the moment a university switches.
  const isPopupDisplayed = isOpen && Boolean(customPopup || LeaddropComponent);
  const popupName = isExitFormOpen
    ? "exit_intent"
    : isDelayFormOpen
      ? "timer"
      : String(variant || "lead_popup");
  const popupWasDisplayed = useRef(false);
  const openPopupName = useRef("");
  useEffect(() => {
    if (isPopupDisplayed && !popupWasDisplayed.current) {
      openPopupName.current = popupName;
      trackPopupOpen({ popup_name: popupName });
    } else if (!isPopupDisplayed && popupWasDisplayed.current) {
      trackCrossClick({ component_name: openPopupName.current || popupName });
    }
    popupWasDisplayed.current = isPopupDisplayed;
  }, [isPopupDisplayed, popupName]);
  // ─── /RUDDERSTACK (new) ───

  /**
   * The custom form wins when one is configured for this surface. Checked BEFORE the
   * legacy guard on purpose: a university may have a custom popup and no legacy popup
   * variant at all, and bailing out on the missing variant would render nothing.
   */
  if (customPopup) {
    return (
      <ConfigLeadFormPopup
        config={customPopup}
        universityId={UNIVERSITY_ID}
        open={isOpen}
        onOpenChange={(next: boolean) => {
          if (!next) dispatch(handleCloseLeaddrop());
        }}
        onClose={() => dispatch(handleCloseLeaddrop())}
        context={defaultValues as Record<string, unknown> | undefined}
        // How the popup was OPENED, carried through unchanged. A brochure CTA sets the
        // download variant on the slice; without these two the custom form would thank
        // the visitor for registering and never hand over the file, which is the same
        // silent failure a missing `variant` causes for the legacy popups.
        variant={variant}
        downloadLink={downloadLink}
      />
    );
  }

  /**
   * Lead Drop Delay Form 5026 — the collector the timer and exit intent open.
   *
   * v1 runs a SECOND dialog for its auto-opens: the timer shows a five-field
   * card behind its own headline, and only Apply Now opens the lead drop that
   * verifies the number. A CTA click never sees it. Before this, both openers
   * landed on the same popup, so the auto-opened surface was the clicked one.
   *
   * NO SWITCH OF ITS OWN. Only one popup can be active at a time, so there is no
   * second row to create and nothing extra to tick: the university's existing
   * "Enable Delay Form" / "Enable Exit Form" and its three intervals are what
   * arm this, and popup 5018 is the design that owns the collector. Any other
   * variant keeps the behaviour it has today, where the auto-open lands on the
   * popup itself.
   *
   * Swapped in here rather than mounted beside the popup because it is the same
   * dialog in the same place: the collector's Apply Now re-opens as a plain
   * REGISTER open carrying the details, which clears `isDelayFormOpen`, so this
   * falls through to 5018 below with a complete prefill and the lead drop
   * auto-continues on the phone. One dialog, v1's two steps.
   *
   * Checked AFTER the custom-popup branch: a CMS-authored form owns its own
   * triggers and must not have a second design put in front of it.
   */
  const isLeadPopup5018 = String(leaddropPopup?.variant ?? "") === "5018";
  if ((isDelayFormOpen || isExitFormOpen) && isLeadPopup5018) {
    return (
      <LeaddropDelay5026Dialog
        open={isOpen}
        onOpenChange={(next: boolean) => {
          if (!next) dispatch(handleCloseLeaddrop());
        }}
        onClose={() => dispatch(handleCloseLeaddrop())}
        formData={formData as ILeadDropPopup | null}
        courses={courses}
        qualification={qualification}
      />
    );
  }

  if (!LeaddropComponent) {
    return null;
  }

  return (
    LeaddropComponent && (
      <LeaddropComponent
        mobileData={mobileData}
        isMobile={isMobile}
        open={isOpen}
        data={{
          formData: formData as ILeadDropPopup | null,
          courses: courses,
          defaultValues: defaultValues,
        }}
        onOpenChange={(open: boolean) => {
          if (!open) {
            dispatch(handleCloseLeaddrop());
          }
        }}
        onClose={() => dispatch(handleCloseLeaddrop())}
        variant={variant}
        lockEnrollmentCourse={effectiveLockEnrollmentCourse}
        enrollmentBoundCourseSelectValue={effectiveBoundCourseSelectValue}
      />
    )
  );
};

export default OpenLeaddrop;
